import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStores } from "../stores";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PrimaryButton from "../components/PrimaryButton";
import BreadcrumbNav from "../components/Breadcrumb";
import FeatureItem from "../components/FeatureItem";
import SectionCard from "../components/SectionCard";
import PropertyDetailSkeleton from "../components/PropertyDetailSkeleton";
import { MESSAGES, formatMessage } from "../utils/messages";
import { shareOrCopy } from "../utils/share";
import { setSEO, resetSEO } from "../utils/seo";
import { isFavorite, toggleFavorite } from "../utils/favorites";
import styles from "./Imovel.module.css";

const CAROUSEL_INTERVAL = 4000;

const TIPO_TO_DISPLAY: Record<string, string> = {
  casa: "Casa",
  apto: "Apartamento",
  loja: "Loja",
  lote: "Lote",
  sala: "Sala",
  outros: "Outros",
};

const TIPO_TO_PATH: Record<string, string> = {
  casa: "casas",
  apto: "apartamentos",
  loja: "lojas",
  lote: "lotes",
  sala: "salas",
  outros: "outros",
};

function toNumber(v: number | null | undefined): number {
  return v ?? 0;
}

function formatCurrency(v: number): string {
  return toNumber(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

const Imovel: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { propertyStore, contactStore } = useStores();
  const { selectedProperty: imovel, isLoading, error } = propertyStore;
  const [isSaved, setIsSaved] = useState(() => (id ? isFavorite(id) : false));
  const [currentImg, setCurrentImg] = useState(0);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const carouselCount = imovel ? imovel.images.length : 0;

  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "" });
  const [leadErrors, setLeadErrors] = useState<Record<string, string>>({});
  const [leadSent, setLeadSent] = useState(false);

  useEffect(() => {
    if (id) propertyStore.loadByCode(id);
    return () => propertyStore.clearSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setCurrentImg(0);
  }, [imovel]);

  useEffect(() => {
    if (!imovel || carouselCount <= 1) return;
    const id = setInterval(() => {
      setCurrentImg((prev) => (prev + 1) % carouselCount);
    }, CAROUSEL_INTERVAL);
    return () => clearInterval(id);
  }, [carouselCount, imovel]);

  useEffect(() => {
    if (!imovel) return;
    const tipoDisplay = TIPO_TO_DISPLAY[imovel.type] || imovel.type;
    const { neighborhood: bairro } = imovel.location;
    const firstPara = imovel.description.split('\n').filter(p => p.trim())[0] || '';
    const imageUrl = imovel.images[imovel.featuredIndex] || imovel.images[0] || '';
    setSEO({
      title: `${tipoDisplay} - ${bairro} | Imóveis Mendicino`,
      description: firstPara.slice(0, 160),
      image: imageUrl,
      url: window.location.href,
    });
    return () => resetSEO();
  }, [imovel]);

  if (isLoading) {
    return (
      <div>
        <Header />
        <div className="bg-light pb-5" style={{ minHeight: "100vh" }}>
          <PropertyDetailSkeleton />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !imovel) {
    return (
      <div>
        <Header />
        <div
          className="container text-center py-5"
          style={{ minHeight: "60vh" }}
        >
          <i className="fas fa-exclamation-circle display-4 text-muted mb-3"></i>
          <h2 className="fw-bold" style={{ color: "#0B2545" }}>
            Não há nenhum imóvel com esse código ainda
          </h2>
          <p className="text-muted mb-4">
            Verifique o código digitado ou explore nossas categorias.
          </p>
          <PrimaryButton variant="primary" href="/">
            Voltar para Home
          </PrimaryButton>
        </div>
        <Footer />
      </div>
    );
  }

  const tipoDisplay = TIPO_TO_DISPLAY[imovel.type] || imovel.type;
  const tipoPath = TIPO_TO_PATH[imovel.type] || imovel.type;
  const { city: cidade, neighborhood: bairro } = imovel.location;

  const imageCount = carouselCount;
  const descParagraphs = imovel.description.split("\n").filter((p) => p.trim());
  const hasCondominio =
    imovel.condoFee !== undefined &&
    imovel.condoFee !== null &&
    imovel.condoFee > 0;
  const hasIptu =
    imovel.propertyTax !== undefined &&
    imovel.propertyTax !== null &&
    imovel.propertyTax > 0;

  const handleShare = async () => {
    const shareText = formatMessage(MESSAGES.SHARE_TEXT, {
      type: tipoDisplay,
      neighborhood: bairro,
      code: imovel.code,
    });
    const result = await shareOrCopy({
      url: window.location.href,
      title: `Imóvel ${tipoDisplay} - ${bairro}`,
      text: shareText,
    });
    if (result === "copied") {
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 2000);
    }
  };

  return (
    <div>
      <Header />

      <div className="container pt-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <BreadcrumbNav
          items={[
            { label: "Início", href: "/" },
            { label: tipoDisplay, href: `/imoveis/${tipoPath}` },
            { label: `Cód. ${imovel.code}` },
          ]}
        />

        <div className="d-flex gap-2">
          <button
            onClick={handleShare}
            className="btn btn-white bg-white border btn-sm text-muted px-2 px-md-3 fw-medium rounded-2 position-relative"
            aria-label="Compartilhar"
          >
            <i className="fas fa-share-alt"></i>
            <span className="d-none d-md-inline ms-2">Compartilhar</span>
            {showCopyTooltip && (
              <span className={styles.tooltip}>Link copiado!</span>
            )}
          </button>
          <button
            className={`btn btn-sm px-3 fw-medium rounded-2 border ${isSaved ? "btn-danger text-white" : "btn-white bg-white text-muted"}`}
            onClick={() => {
              const updated = toggleFavorite(imovel.code);
              setIsSaved(updated.includes(imovel.code));
            }}
          >
            <i className={`${isSaved ? "fas" : "far"} fa-heart me-2`}></i>{" "}
            {isSaved ? "Salvo" : "Salvar"}
          </button>
        </div>
      </div>

      <header className="container my-3">
        <h1 className="fw-bold h2" style={{ color: "#0B2545" }}>
          {tipoDisplay} - {bairro}
        </h1>
        <p className="text-muted small d-flex align-items-center gap-1">
          <i className="fas fa-map-marker-alt text-secondary"></i> {bairro},{" "}
          {cidade}
        </p>
      </header>

      {imageCount > 0 && (
        <section className="container mb-4">
          <div className="row g-2">
            <div className="col-12 col-md-8">
              <div
                className={`position-relative overflow-hidden rounded-3 ${styles.mainCarousel}`}
              >
                <div
                  style={{
                    display: "flex",
                    height: "100%",
                    transform: `translateX(-${currentImg * 100}%)`,
                    transition: "transform 0.5s ease-in-out",
                  }}
                >
                  {imovel.images.slice(0, imageCount).map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt={`${imovel.code} - ${idx + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </div>

                {imageCount > 1 && (
                  <>
                    <button
                      type="button"
                      className="btn position-absolute top-50 start-0 translate-middle-y ms-2 p-0 d-flex align-items-center justify-content-center shadow-sm"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        borderRadius: "50%",
                      }}
                      onClick={() =>
                        setCurrentImg(
                          (prev) => (prev - 1 + imageCount) % imageCount,
                        )
                      }
                    >
                      <i
                        className="fas fa-chevron-left"
                        style={{ color: "#333" }}
                      ></i>
                    </button>
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 d-flex align-items-center justify-content-center shadow-sm"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        borderRadius: "50%",
                      }}
                      onClick={() =>
                        setCurrentImg((prev) => (prev + 1) % imageCount)
                      }
                    >
                      <i
                        className="fas fa-chevron-right"
                        style={{ color: "#333" }}
                      ></i>
                    </button>
                  </>
                )}
              </div>
            </div>

            {imageCount > 1 && (
              <div className="col-12 col-md-4">
                <div className={styles.thumbnails}>
                  <div
                    className={styles.thumb}
                    onClick={() => setCurrentImg(1)}
                  >
                    <img
                      src={imovel.images[1]}
                      alt=""
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  </div>

                  {imageCount > 2 && (
                    <div
                      className={styles.thumb}
                      onClick={() => setCurrentImg(2)}
                    >
                      <img
                        src={imovel.images[2]}
                        alt=""
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          backdropFilter: "blur(4px)",
                          backgroundColor: "rgba(0,0,0,0.25)",
                        }}
                      >
                        <span
                          className="text-white fw-bold"
                          style={{
                            fontSize: "2rem",
                            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                          }}
                        >
                          +{imageCount - 3}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <main className="container">
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <SectionCard>
              <h5 className="fw-bold mb-4 small text-muted text-uppercase">
                Características Principais
              </h5>
              <div className="row g-3 row-cols-2 row-cols-sm-4 text-center">
                <FeatureItem
                  icon="fas fa-ruler-combined"
                  subtitle="Área Total"
                  value={`${imovel.builtArea ?? "-"} m²`}
                  variant="icon-top"
                />
                <FeatureItem
                  icon="fas fa-bed"
                  subtitle="Dormitórios"
                  value={`${imovel.bedrooms} Quartos`}
                  variant="icon-top"
                />
                <FeatureItem
                  icon="fas fa-car"
                  subtitle="Vagas"
                  value={`${imovel.parkingSpots} Vagas`}
                  variant="icon-top"
                />
                <FeatureItem
                  icon="fas fa-bath"
                  subtitle="Banheiros"
                  value={`${imovel.bathrooms}`}
                  variant="icon-top"
                />
              </div>
              <div className="row g-3 row-cols-2 row-cols-sm-4 text-center mt-2">
                {imovel.suites > 0 && (
                  <FeatureItem
                    icon="fas fa-bath"
                    subtitle="Suítes"
                    value={`${imovel.suites}`}
                    variant="icon-top"
                  />
                )}
                <FeatureItem
                  icon="fas fa-check-circle"
                  subtitle="Habite-se"
                  value={imovel.occupancyPermit === "s" ? "Sim" : "Não"}
                  variant="icon-top"
                />
              </div>
            </SectionCard>

            <SectionCard>
              <h5 className="fw-bold mb-3" style={{ color: "#0B2545" }}>
                Descrição do Imóvel
              </h5>
              <div className="text-muted lh-lg fs-6">
                {descParagraphs.map((para, idx) => (
                  <p key={idx} className="mb-3">
                    {para}
                  </p>
                ))}
              </div>
            </SectionCard>

            <SectionCard>
              <h5 className="fw-bold mb-3" style={{ color: "#0B2545" }}>
                Localização
              </h5>
              <div
                className="position-relative rounded-3 overflow-hidden"
                style={{ height: "300px" }}
              >
                <iframe
                  title={bairro}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(`${bairro}, ${cidade}`)}&output=embed`}
                  className="w-100 h-100"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div
                  className="position-absolute bottom-0 start-0 m-3 p-3 bg-white rounded-3 shadow border text-start"
                  style={{ maxWidth: "280px" }}
                >
                  <h6 className="fw-bold mb-1 small text-dark">Endereço</h6>
                  <p className="text-muted m-0" style={{ fontSize: "0.75rem" }}>
                    {bairro}, {cidade}
                  </p>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="col-12 col-lg-4">
            <div className="sticky-top" style={{ top: "100px", zIndex: 9 }}>
              <div className="bg-white rounded-3 border p-4 shadow-sm mb-4">
                <div className="d-flex align-items-baseline justify-content-between mb-2">
                  <span
                    className="badge text-muted bg-light border px-2 py-1"
                    style={{ fontSize: "0.7rem" }}
                  >
                    CÓDIGO: {imovel.code}
                  </span>
                  <span
                    className="small text-muted fw-bold text-uppercase"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Venda
                  </span>
                </div>

                <h3 className="fw-bold mb-3" style={{ color: "#0B2545" }}>
                  {formatCurrency(imovel.price)}
                </h3>

                {(hasCondominio || hasIptu) && (
                  <div className="row g-2 border-bottom pb-4 mb-4 text-muted small">
                    {hasCondominio && (
                      <div className="col-6 d-flex justify-content-between">
                        <span>Condomínio</span>
                        <strong className="text-dark">
                          {formatCurrency(imovel.condoFee ?? 0)}/mês
                        </strong>
                      </div>
                    )}
                    {hasIptu && (
                      <div
                        className={`col-6 d-flex justify-content-between ${hasCondominio ? "ps-3 border-start" : ""}`}
                      >
                        <span>IPTU</span>
                        <strong className="text-dark">
                          {formatCurrency(imovel.propertyTax ?? 0)}/mês
                        </strong>
                      </div>
                    )}
                  </div>
                )}

                <h6 className="fw-bold mb-3 text-dark small text-uppercase">
                  Tenho interesse neste imóvel
                </h6>
                {leadSent && (
                  <div className="alert alert-success py-1 small mb-3">
                    <i className="fas fa-check-circle me-1"></i>
                    Mensagem enviada com sucesso!
                  </div>
                )}
                {contactStore.lastError && (
                  <div className="alert alert-danger py-1 small mb-3">
                    <i className="fas fa-exclamation-circle me-1"></i>
                    {contactStore.lastError}
                  </div>
                )}
                <form
                  className="row g-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setLeadSent(false);
                    const errors: Record<string, string> = {};
                    if (
                      !leadForm.name.trim() ||
                      leadForm.name.trim().length < 2
                    )
                      errors.name = "Informe seu nome";
                    if (
                      !leadForm.email.trim() ||
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadForm.email.trim())
                    )
                      errors.email = "E-mail inválido";
                    const phoneDigits = leadForm.phone.replace(/\D/g, "");
                    if (!leadForm.phone.trim() || phoneDigits.length < 8)
                      errors.phone = "Telefone com pelo menos 8 dígitos";
                    setLeadErrors(errors);
                    if (Object.keys(errors).length > 0) return;
                    const ok = await contactStore.sendLead({
                      ...leadForm,
                      message: `Tenho interesse no imóvel ${imovel.code} (${imovel.type})`,
                      propertyCode: imovel.code,
                    });
                    if (ok) {
                      setLeadSent(true);
                      setLeadForm({ name: "", email: "", phone: "" });
                      setLeadErrors({});
                    }
                  }}
                >
                  <div className="col-12 mb-2">
                    <input
                      type="text"
                      className={`form-control py-2 text-muted small ${leadErrors.name ? "is-invalid" : ""}`}
                      placeholder="Nome Completo"
                      value={leadForm.name}
                      onChange={(e) => {
                        setLeadForm({ ...leadForm, name: e.target.value });
                        if (leadErrors.name)
                          setLeadErrors((p) => ({ ...p, name: "" }));
                      }}
                    />
                    {leadErrors.name && (
                      <div className="invalid-feedback">{leadErrors.name}</div>
                    )}
                  </div>
                  <div className="col-12 mb-2">
                    <input
                      type="email"
                      className={`form-control py-2 text-muted small ${leadErrors.email ? "is-invalid" : ""}`}
                      placeholder="E-mail"
                      value={leadForm.email}
                      onChange={(e) => {
                        setLeadForm({ ...leadForm, email: e.target.value });
                        if (leadErrors.email)
                          setLeadErrors((p) => ({ ...p, email: "" }));
                      }}
                    />
                    {leadErrors.email && (
                      <div className="invalid-feedback">{leadErrors.email}</div>
                    )}
                  </div>
                  <div className="col-12 mb-3">
                    <input
                      type="tel"
                      className={`form-control py-2 text-muted small ${leadErrors.phone ? "is-invalid" : ""}`}
                      placeholder="Telefone (WhatsApp)"
                      value={leadForm.phone}
                      onChange={(e) => {
                        setLeadForm({ ...leadForm, phone: e.target.value });
                        if (leadErrors.phone)
                          setLeadErrors((p) => ({ ...p, phone: "" }));
                      }}
                    />
                    {leadErrors.phone && (
                      <div className="invalid-feedback">{leadErrors.phone}</div>
                    )}
                  </div>
                  <div className="col-12">
                    <PrimaryButton
                      variant="primary"
                      fullWidth
                      type="submit"
                      disabled={contactStore.isSending}
                    >
                      {contactStore.isSending
                        ? "Enviando..."
                        : "Enviar Mensagem"}
                    </PrimaryButton>
                  </div>
                </form>

                <div className="position-relative text-center my-3">
                  <hr className="text-muted opacity-25" />
                  <span className="position-absolute top-50 start-50 translate-middle bg-white px-2 text-muted small">
                    ou
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    contactStore.openWhatsApp(
                      formatMessage(MESSAGES.WHATSAPP_INTEREST, {
                        type: tipoDisplay,
                        neighborhood: bairro,
                        code: imovel.code,
                      }),
                    )
                  }
                  className="btn text-white w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 rounded-2 border-0"
                  style={{ backgroundColor: "#1E7E34" }}
                >
                  <i className="fab fa-whatsapp fs-5"></i> Conversar por
                  WhatsApp
                </button>

                <p
                  className="text-center text-muted small mt-3 mb-0"
                  style={{ fontSize: "0.65rem" }}
                >
                  Ao enviar, você concorda com nossos{" "}
                  <a href="/" className="text-decoration-underline text-muted">
                    Termos de Uso
                  </a>
                  .
                </p>
              </div>

              <div className="bg-white rounded-3 border p-3 shadow-sm d-flex align-items-center gap-3">
                <div
                  className="rounded-circle shadow-sm d-flex align-items-center justify-content-center bg-light"
                  style={{ width: "52px", height: "52px", color: "#0B2545" }}
                >
                  <i className="fas fa-user-tie fs-5"></i>
                </div>
                <div>
                  <h6
                    className="fw-bold mb-0 text-dark"
                    style={{ fontSize: "0.9rem" }}
                  >
                    José Américo Mendicino
                  </h6>
                  <span
                    className="text-muted d-block"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Corretor de Imóveis
                  </span>
                  <small
                    className="text-uppercase text-secondary fw-bold"
                    style={{ fontSize: "0.65rem" }}
                  >
                    CRECI/CRC
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <div className="mt-5">
        <Footer />
      </div>
    </div>
  );
};

export default observer(Imovel);
